
import {
  defineComponent, ref, computed, watch, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import Loader from '@/components/layout/Loader.vue';
import SpmMessage from '@/components/SpmUI/SpmMessage.vue';

export default defineComponent({
  name: 'FileSelectStep',

  components: {
    Button,
    Textarea,
    Loader,
    SpmMessage,
  },

  props: {
    importType: {
      type: String,
      required: true,
    },

    accept: {
      type: String,
      default: '.csv,.xlsx,.txt',
    },

    maxFileSize: {
      type: Number,
      default: 70000000, // 70MB
    },

    showResources: {
      type: Boolean,
      default: true,
    },

    modelValue: {
      type: Object as PropType<{ file: File | null; pastedText: string; useCopyPaste: boolean }>,
      default: () => ({ file: null, pastedText: '', useCopyPaste: false }),
    },

    /** Whether copy-paste mode is allowed */
    allowCopyPaste: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:modelValue', 'valid-change', 'open-help'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const loading = ref(false);
    const fileInput = ref<HTMLInputElement | null>(null);
    const textareaRef = ref<InstanceType<typeof Textarea> | null>(null);
    const isDragOver = ref(false);
    const selectedFile = ref<File | null>(props.modelValue?.file || null);
    const pastedText = ref(props.modelValue?.pastedText || '');
    const useCopyPaste = ref(props.modelValue?.useCopyPaste || false);
    const fileError = ref<string | null>(null);
    const pasteError = ref<string | null>(null);

    const isValid = computed(() => {
      if (useCopyPaste.value) {
        return !!pastedText.value.trim() && !pasteError.value;
      }
      return !!selectedFile.value && !fileError.value;
    });

    // Display accepted formats based on accept prop
    const acceptedFormatsDisplay = computed(() => {
      const formats = props.accept
        .split(',')
        .map((ext) => ext.replace('.', '').toUpperCase().trim());
      return formats.join(', ');
    });

    // Check if this is a zip-only upload
    const isZipOnly = computed(() => props.accept.trim() === '.zip');

    // Dynamic upload title based on accepted formats
    const uploadTitle = computed(() => {
      if (isZipOnly.value) {
        return t('import.selectStep.upload.titleZip');
      }
      return t('import.selectStep.upload.title');
    });

    // Dynamic upload subtitle based on accepted formats
    const uploadSubtitle = computed(() => {
      if (isZipOnly.value) {
        return t('import.selectStep.upload.subtitleZip');
      }
      return t('import.selectStep.upload.subtitle');
    });

    // Emit validity changes
    watch(isValid, (newVal) => {
      emit('valid-change', newVal);
    }, { immediate: true });

    // Emit model value changes
    watch([selectedFile, pastedText, useCopyPaste], () => {
      emit('update:modelValue', {
        file: selectedFile.value,
        pastedText: pastedText.value,
        useCopyPaste: useCopyPaste.value,
      });
    }, { deep: true });

    // Sync with modelValue prop
    watch(() => props.modelValue, (newVal) => {
      if (newVal) {
        if (newVal.file !== selectedFile.value) {
          selectedFile.value = newVal.file;
        }
        if (newVal.pastedText !== pastedText.value) {
          pastedText.value = newVal.pastedText;
        }
        if (newVal.useCopyPaste !== useCopyPaste.value) {
          useCopyPaste.value = newVal.useCopyPaste;
        }
      }
    }, { deep: true });

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    };

    const getFileIcon = (fileName: string): string => {
      const extension = fileName.split('.').pop()?.toLowerCase();
      switch (extension) {
        case 'csv':
        case 'txt':
          return 'fas fa-file-csv file-type-icon csv';
        case 'xlsx':
        case 'xls':
          return 'fas fa-file-excel file-type-icon excel';
        default:
          return 'fas fa-file file-type-icon';
      }
    };

    const triggerFileInput = () => {
      fileInput.value?.click();
    };

    const validateFile = (file: File): boolean => {
      fileError.value = null;

      if (file.size > props.maxFileSize) {
        fileError.value = t('import.selectStep.errors.fileTooBig', {
          max: formatFileSize(props.maxFileSize),
        });
        return false;
      }

      const acceptedExtensions = props.accept.split(',').map((ext) => ext.trim().toLowerCase());
      const fileName = file.name.toLowerCase();
      const isValidType = acceptedExtensions.some((ext) => fileName.endsWith(ext.replace('.', '')));

      if (!isValidType) {
        fileError.value = t('import.selectStep.errors.invalidType', {
          types: props.accept,
        });
        return false;
      }

      return true;
    };

    const processFile = (file: File) => {
      if (validateFile(file)) {
        selectedFile.value = file;
      }
    };

    const onFileSelect = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        processFile(file);
      }
      target.value = '';
    };

    const onDrop = (event: DragEvent) => {
      isDragOver.value = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        processFile(file);
      }
    };

    const onDragOver = () => {
      isDragOver.value = true;
    };

    const onDragLeave = () => {
      isDragOver.value = false;
    };

    const removeFile = () => {
      selectedFile.value = null;
      fileError.value = null;
    };

    const validatePastedText = () => {
      pasteError.value = null;

      if (!pastedText.value.trim()) {
        return;
      }

      const lines = pastedText.value.split('\n').filter((line) => line.trim());

      // Must have at least 2 lines (header + data)
      if (lines.length < 2) {
        pasteError.value = t('import.selectStep.errors.minimumTwoLines');
        return;
      }

      const hasSeparator = /[,;\t]/.test(pastedText.value);
      if (!hasSeparator) {
        pasteError.value = t('import.selectStep.errors.invalidFormat');
      }
    };

    const getLineCount = (): number => {
      if (!pastedText.value) return 0;
      return pastedText.value.split('\n').filter((line) => line.trim()).length;
    };

    const toggleMode = () => {
      useCopyPaste.value = !useCopyPaste.value;
      // Reset errors when switching
      fileError.value = null;
      pasteError.value = null;
    };

    return {
      t,
      loading,
      fileInput,
      textareaRef,
      isDragOver,
      selectedFile,
      pastedText,
      useCopyPaste,
      fileError,
      pasteError,
      acceptedFormatsDisplay,
      uploadTitle,
      uploadSubtitle,
      formatFileSize,
      getFileIcon,
      triggerFileInput,
      onFileSelect,
      onDrop,
      onDragOver,
      onDragLeave,
      removeFile,
      validatePastedText,
      getLineCount,
      toggleMode,
    };
  },
});
